import CompetitionService from '@/services/CompetitionService'
import moment from 'moment-timezone'

export default {
  namespaced: true,
  state: {
    detailedClassifications: [],
    feedbackBreakdown: [],
    summaries: {},
    wordClouds: {},
    wordCloudQuantity: 40,
    filters: null,
    skip: 0,
    limit: 20,
    count: 0,
    mentions: {},
    positiveClassificationsByCategory: {},
    negativeClassificationsByCategory: {},
    feedbackCountBreakdown: {},
    feedbackCount: 0,
    totalFeedbackCount: 0
  },
  getters: {
    selectDetailedClassifications: (state) => state.detailedClassifications,
    selectFeedbackBreakdown: (state) => state.feedbackBreakdown,
    selectSummaries: (state) => state.summaries,
    selectFilters: (state) => state.filters,
    selectSkip: (state) => state.skip,
    selectLimit: (state) => state.limit,
    selectCount: (state) => state.count,
    selectMentions: (state) => state.mentions,
    selectPositiveClassificationsByCategory: (state) => state.positiveClassificationsByCategory,
    selectNegativeClassificationsByCategory: (state) => state.negativeClassificationsByCategory,
    selectFeedbackCountBreakdown: (state) => state.feedbackCountBreakdown,
    selectFeedbackCount: (state) => state.feedbackCount,
    selectTotalFeedbackCount: (state) => state.totalFeedbackCount,
    selectGranularity: (state, getters) => {
      const dateRange = getters.selectFilters?.dateRange
      if (!dateRange) return 'day'
      const dateSpread = moment(dateRange[1]).diff(moment(dateRange[0]), 'd')
      let granularity
      if (dateSpread <= 14) granularity = 'day'
      else if (dateSpread <= 98) granularity = 'week'
      else if (dateSpread <= 420) granularity = 'month'
      else granularity = 'year'
      return granularity
    },
    selectBrands: (state, getters, rootState, rootGetters) => {
      const activeCompany = rootGetters['company/selectActiveCompany']
      return rootGetters['competition/selectCompetitors'].reduce(
        (acc, curr) => {
          acc.push({ _id: curr._id, name: curr.name })
          return acc
        },
        [{ _id: activeCompany._id, name: activeCompany.name }]
      )
    },
    selectBrandOptions: (state, getters) =>
      getters.selectBrands.map((b) => ({ label: b.name, value: b._id })),
    selectWordCloud: (state) => {
      const quantity = state.wordCloudQuantity
      const halfQuantity = quantity / 2
      const posWC = state.wordClouds.positiveWordCloud || []
      const negWC = state.wordClouds.negativeWordCloud || []

      let wordcloud = []
      if (posWC.length > halfQuantity && negWC.length > halfQuantity) {
        wordcloud = [...posWC.slice(0, halfQuantity), ...negWC.slice(0, halfQuantity)]
      } else if (posWC.length < halfQuantity && negWC.length < halfQuantity) {
        wordcloud = [...posWC, ...negWC]
      } else if (posWC?.length > halfQuantity) {
        const negQuantity = negWC.length
        wordcloud = [...negWC, ...posWC.slice(0, quantity - negQuantity)]
      } else if (negWC?.length > halfQuantity) {
        const posQuantity = posWC.length
        wordcloud = [...posWC, ...negWC.slice(0, quantity - posQuantity)]
      }

      const totalCount = wordcloud.reduce((acc, curr) => {
        acc += curr.count
        return acc
      }, 0)
      return wordcloud.reduce((acc, curr) => {
        acc.push({
          category: curr.word,
          value: ((curr.count / totalCount) * 100).toFixed(2),
          sentiment: curr.sentiment
        })
        return acc
      }, [])
    },
    selectAmKey: (state, getters, rootState, rootGetters) =>
      rootGetters['selectConfigByName']('AMKEY'),
    selectDetailsFilterCategory(state, getters, rootState, rootGetters) {
      return rootGetters['insightsv2/selectCategory'](state.filters?.category)
    }
  },
  mutations: {
    SET_FILTERS(state, filters) {
      state.filters = filters
    },
    SET_DETAILED_CLASSIFICATIONS(state, detailedClassifications) {
      state.detailedClassifications = detailedClassifications
    },
    SET_FEEDBACK_BREAKDOWN(state, feedbackBreakdown) {
      state.feedbackBreakdown = feedbackBreakdown
    },
    SET_SUMMARIES(state, summaries) {
      state.summaries = summaries
    },
    SET_WORDCLOUDS(state, wordClouds) {
      state.wordClouds = wordClouds
    },
    SET_SKIP(state, skip) {
      state.skip = skip
    },
    SET_COUNT(state, count) {
      state.count = count
    },
    SET_MENTIONS(state, mentions) {
      state.mentions = mentions
    },
    SET_POSITIVE_CLASSIFICATIONS_BY_CATEGORY(state, classifications) {
      state.positiveClassificationsByCategory = classifications
    },
    SET_NEGATIVE_CLASSIFICATIONS_BY_CATEGORY(state, classifications) {
      state.negativeClassificationsByCategory = classifications
    },
    SET_FEEDBACK_COUNT_BREAKDOWN(state, feedbackCountBreakdown) {
      state.feedbackCountBreakdown = feedbackCountBreakdown
    },
    SET_FEEDBACK_COUNT(state, feedbackCount) {
      state.feedbackCount = feedbackCount
    },
    SET_TOTAL_FEEDBACK_COUNT(state, totalFeedbackCount) {
      state.totalFeedbackCount = totalFeedbackCount
    }
  },
  actions: {
    async applyFilters({ commit, dispatch }, filters) {
      dispatch('clearDetailedClassificationsState')
      commit('SET_FILTERS', filters)
    },

    async clearDetailedClassificationsState({ commit }) {
      commit('SET_SKIP', 0)
      commit('SET_DETAILED_CLASSIFICATIONS', [])
    },

    async formatDetailFilters({ getters, rootState, rootGetters }) {
      const filters = getters.selectFilters
      const payloadFilters = {
        createdAtRange: filters.dateRange || [
          moment().startOf('d').subtract(30, 'd').utc().format(),
          moment().endOf('day').utc().format()
        ],
        companyIds: [rootGetters['company/selectActiveCompany']._id],
        isSummary: false // this is only true if the request is coming from the summary page
      }

      if (filters.locations) {
        payloadFilters.locationIds = Array.isArray(filters.locations)
          ? filters.locations
          : [filters.locations]
      }

      if (filters.groups && filters.groups.length) {
        payloadFilters.groupIds = Array.isArray(filters.groups) ? filters.groups : [filters.groups]
      }

      if (filters.sentiment) {
        payloadFilters.sentiment = filters.sentiment
      }

      if (filters.category) {
        // if parent category, pass all child id's too
        const isSubcategory = rootGetters['insightsv2/selectCategory'](filters.category)?.path
        if (isSubcategory) {
          payloadFilters.categoryIds = [filters.category]
        } else {
          payloadFilters.categoryIds = rootGetters['insightsv2/selectNonOtherCategories'].reduce(
            (acc, curr) => {
              if (curr.path === filters.category || curr._id === filters.category) {
                acc.push(curr._id)
              }
              return acc
            },
            []
          )
        }
      }

      if (filters.brand) {
        payloadFilters.competitorIds = [filters.brand]
      }

      return payloadFilters
    },

    async fetchFeedbackData({ commit, getters, dispatch }, { loadMore = false } = {}) {
      const filters = getters.selectFilters
      const response = await CompetitionService.fetchFeedbackData({
        filters: await dispatch('formatDetailFilters'),
        ...(filters.keywords?.length ? { search: filters.keywords } : {}),
        limit: getters.selectLimit,
        skip: getters.selectSkip
      })

      // Only append data if loadMore is true, otherwise replace the data
      if (loadMore) {
        commit(
          'SET_DETAILED_CLASSIFICATIONS',
          getters.selectDetailedClassifications.concat(response.body.data.feedback)
        )
        commit('SET_SKIP', getters.selectDetailedClassifications.length)
      } else {
        commit('SET_DETAILED_CLASSIFICATIONS', response.body.data.feedback)
        commit('SET_SKIP', response.body.data.feedback.length)
      }

      commit('SET_COUNT', response.body.data.count)
    },

    async fetchBreakdown({ commit, getters, dispatch, rootGetters }) {
      const filters = getters.selectFilters
      const granularity = getters.selectGranularity
      const dateRange = filters.dateRange
      const categories = rootGetters['insightsv2/selectNonOtherCategories']

      // Initialize category classifications
      let positiveClassificationsByCategory = {}
      let negativeClassificationsByCategory = {}
      categories.forEach((cat) => {
        positiveClassificationsByCategory[cat._id] = { name: cat.name, classifications: [] }
        negativeClassificationsByCategory[cat._id] = { name: cat.name, classifications: [] }
      })

      const response = await CompetitionService.fetchBreakdown({
        filters: await dispatch('formatDetailFilters'),
        ...(filters.keywords?.length ? { search: filters.keywords } : {}),
        getAllData: true
      })

      // Transform feedbackBreakdown into brand counts
      const brandCounts = response.body.data.feedbackBreakdown.reduce((acc, item) => {
        acc[item.competitorId] = {
          brandCount: item.feedbackCount,
          reviewCount: 0
        }
        return acc
      }, {})

      let mentions = {}
      response.body.data.mentionsOverTime.forEach((item) => {
        const date =
          granularity === 'day'
            ? item.date
            : granularity === 'week'
            ? moment(item.date).startOf('w').format('YYYY-MM-DD')
            : granularity === 'month'
            ? moment(item.date).startOf('M').format('YYYY-MM-DD')
            : moment(item.date).startOf('year').format('YYYY-MM-DD')

        if (mentions[date]) {
          mentions[date].POSITIVE += item.positiveMentionCount || 0
          mentions[date].NEGATIVE += item.negativeMentionCount || 0
        } else {
          mentions[date] = {
            POSITIVE: item.positiveMentionCount || 0,
            NEGATIVE: item.negativeMentionCount || 0
          }
        }
      })

      // Process all items in feedbackBreakdown (don't skip first item for competition data)
      response.body.data.feedbackBreakdown.forEach((item) => {
        const competitorId = item.competitorId

        if (item.categoryBreakdown && Array.isArray(item.categoryBreakdown)) {
          item.categoryBreakdown.forEach((category) => {
            // Only process categories that exist in our insights categories (exclude "Other" categories)
            if (!rootGetters['insightsv2/selectOtherCategoryIds'].includes(category.categoryId)) {
              if (category.positiveMentionCount > 0) {
                const classification = {
                  category: category.categoryId,
                  sentiment: 'POSITIVE',
                  brandIds: [competitorId],
                  text: category.categoryName,
                  positiveMentionCount: category.positiveMentionCount
                }
                if (positiveClassificationsByCategory[category.categoryId]) {
                  positiveClassificationsByCategory[category.categoryId].classifications.push(
                    classification
                  )
                }
              }
              if (category.negativeMentionCount > 0) {
                const classification = {
                  category: category.categoryId,
                  negativeMentionCount: category.negativeMentionCount,
                  sentiment: 'NEGATIVE',
                  brandIds: [competitorId],
                  text: category.categoryName
                }
                if (negativeClassificationsByCategory[category.categoryId]) {
                  negativeClassificationsByCategory[category.categoryId].classifications.push(
                    classification
                  )
                }
              }
            }
          })
        }
      })

      let startDate = moment(dateRange[0]).startOf(granularity)
      const finalDate = moment(dateRange[1]).startOf(granularity)
      let completeMentions = {}

      while (startDate.isBefore(finalDate) || startDate.isSame(finalDate)) {
        const formattedDate = startDate.format('YYYY-MM-DD')
        completeMentions[formattedDate] = mentions[formattedDate] || {
          POSITIVE: 0,
          NEGATIVE: 0
        }
        startDate.add(1, granularity)
      }

      const feedbackCount = response.body.data.feedbackBreakdown.reduce(
        (acc, item) => acc + (item.feedbackCount || 0),
        0
      )

      commit('SET_POSITIVE_CLASSIFICATIONS_BY_CATEGORY', positiveClassificationsByCategory)
      commit('SET_NEGATIVE_CLASSIFICATIONS_BY_CATEGORY', negativeClassificationsByCategory)
      commit('SET_FEEDBACK_BREAKDOWN', response.body.data.feedbackBreakdown)
      commit('SET_TOTAL_FEEDBACK_COUNT', response.body.data.totalFeedbackCount || feedbackCount)
      commit('SET_MENTIONS', completeMentions)
      commit('SET_FEEDBACK_COUNT_BREAKDOWN', brandCounts)
      commit('SET_FEEDBACK_COUNT', feedbackCount)
    },

    async fetchSummaries({ commit, dispatch, getters }) {
      const filters = getters.selectFilters
      const response = await CompetitionService.fetchSummaries({
        filters: await dispatch('formatDetailFilters'),
        ...(filters.keywords?.length ? { search: filters.keywords } : {})
      })
      const {
        positiveSummary,
        negativeSummary,
        positiveWordCloud,
        negativeWordCloud,
        summariesUnavailable
      } = response.body.data
      commit('SET_SUMMARIES', { positiveSummary, negativeSummary, summariesUnavailable })
      commit('SET_WORDCLOUDS', { positiveWordCloud, negativeWordCloud })
    }
  }
}
